<template>
    <div class="confirmation-container">
        <h2>Configuration Set Details</h2>
        <dl>
            <div>
                <dt>Name:</dt>
                <dd data-test-id="configuration-set-name-text">{{ configurationSet.name }}</dd>
            </div>
            <div>
                <dt>Distribution Name:</dt>
                <dd data-test-id="distribution-name-text">{{ distribution.name }}</dd>
            </div>
            <template v-if="configurationSet.pli_interval_type">
                <div class="has-image image-pli">
                    <dt>PLI Interval:</dt>
                    <dd>
                        Every
                        <span class="spaced-spans">
                            <span data-test-id="pli-interval-value-text">{{ pliInterval.value }}</span>
                            <span data-test-id="pli-interval-unit-text">{{ pliInterval.unit }}</span>
                            <span
                                v-if="
                                    configurationSet.pli_interval_time_jitter !== null &&
                                    configurationSet.pli_interval_time_jitter !== 0
                                "
                            >
                                (<span data-test-id="pli-interval-jitter-value">{{
                                    configurationSet.pli_interval_time_jitter
                                }}</span
                                >% Jittering)</span
                            >
                        </span>
                    </dd>
                </div>
            </template>
            <template v-if="configurationSet.frequency_set">
                <div class="has-image image-frequency-set">
                    <dt class="label">Frequency Set:</dt>
                    <dd data-test-id="frequency-set-name-text">
                        {{ configurationSet.frequency_set.name }}
                        <frequency-set-type-badge
                            :type="configurationSet.frequency_set.type"
                            data-test-id="frequency-set-type-badge"
                        />
                    </dd>
                </div>
                <div class="nested">
                    <dt class="label">Power:</dt>
                    <dd data-test-id="frequency-set-max-power-text">{{ configurationSet.frequency_set.max_power }}</dd>
                </div>
                <div class="nested">
                    <dt class="label">Bandwidth:</dt>

                    <dd data-test-id="frequency-set-bandwidth-text">
                        {{ configurationSet?.frequency_set.bandwidth }}
                    </dd>
                </div>
                <div class="multiline nested">
                    <dt class="label">Control Channels:</dt>
                    <dd>
                        <ul>
                            <li
                                v-for="(channel, index) in controlChannels"
                                :key="index"
                            >
                                <span :data-test-id="`frequency-set-control-channel-text-${index}`">{{
                                    channel.frequency
                                }}</span>
                            </li>
                        </ul>
                    </dd>
                </div>
                <div class="multiline nested">
                    <dt class="label">Data Channels:</dt>
                    <dd>
                        <ul>
                            <li
                                v-for="(channel, index) in dataChannels"
                                :key="index"
                            >
                                <span :data-test-id="`frequency-set-data-channel-text-${index}`">{{
                                    channel.frequency
                                }}</span>
                            </li>
                        </ul>
                    </dd>
                </div>
            </template>
            <template v-if="configurationSet.encryption_key">
                <div class="has-image image-encryption-key">
                    <dt class="label">Default Encryption Key:</dt>
                    <dd data-test-id="encryption-key-name-text">{{ configurationSet.encryption_key.name }}</dd>
                </div>
            </template>
            <template v-if="distribution.groups.length > 0">
                <div
                    class="multiline has-image image-groups"
                    data-test-id="groups-section"
                >
                    <dt class="label">
                        Groups (<span data-test-id="group-count-text">{{ distribution.groups.length }}</span
                        >)
                    </dt>
                    <dd class="group-list">
                        <span
                            v-for="({ name }, index) in groupsSorted"
                            :key="index"
                            :data-test-id="`group-name-text-${index}`"
                            >{{ name }}</span
                        >
                    </dd>
                </div>
            </template>
            <template v-if="distribution.assets.length > 0">
                <div
                    class="multiline has-image image-groups"
                    data-test-id="assets-section"
                >
                    <dt class="label">
                        AdvancedRelays (<span data-test-id="asset-count-text">{{ distribution.assets.length }}</span
                        >)
                    </dt>
                    <dd class="asset">
                        <span
                            v-for="({ name }, index) in assetsSorted"
                            :key="index"
                            :data-test-id="`asset-name-text-${index}`"
                            >{{ name }}</span
                        >
                    </dd>
                </div>
            </template>
            <template v-if="!distribution.distribute_now">
                <div class="multiline has-image image-dates">
                    <dt>Distribution Time</dt>
                    <dd data-test-id="distribute-at-text">{{ distributeAtFormatted }}</dd>
                </div>
                <div class="remove-top-gap nested">
                    <dt class="label">Time Zone:</dt>
                    <dd data-test-id="distribute-at-timezone-text">{{ timezoneLabel }}</dd>
                </div>
            </template>
            <template v-else>
                <div class="multiline has-image image-dates">
                    <dt>Distribution Time</dt>
                    <dd data-test-id="distribute-at-text">Immediately</dd>
                </div>
            </template>

            <div class="multiline nested">
                <dt class="label">Activation Time</dt>
                <dd
                    v-if="!distribution.activate_now"
                    data-test-id="activate-at-text"
                >
                    {{ activateAtFormatted }}
                </dd>
                <dd
                    v-else
                    data-test-id="activate-at-text"
                >
                    Immediately upon device receipt
                </dd>
            </div>
            <div
                v-if="!distribution.activate_now"
                data-test-id="activate-at-timezone-container"
                class="remove-top-gap nested"
            >
                <dt class="label">Time Zone:</dt>
                <dd data-test-id="activate-at-timezone-text">{{ timezoneLabel }}</dd>
            </div>

            <div class="multiline nested">
                <dt class="label">Expiration Time</dt>
                <dd data-test-id="expire-at-text">{{ expireAtFormatted }}</dd>
            </div>
            <div
                v-if="distribution.expire_at"
                class="remove-top-gap nested"
            >
                <dt class="label">Time Zone:</dt>
                <dd data-test-id="expire-at-timezone-text">{{ timezoneLabel }}</dd>
            </div>
        </dl>
    </div>
</template>

<script>
import { sortBy } from 'lodash'
import { formatPliInterval } from '@/utils/pli-interval.js'
import FrequencySetTypeBadge from '@/components/FrequencySetTypeBadge.vue'

function objectHasAllProperties(value, keys) {
    return keys.every((property) => Object.hasOwn(value, property))
}
export default {
    components: {
        FrequencySetTypeBadge,
    },

    props: {
        configurationSet: {
            type: Object,
            required: true,
            validator: (value) => {
                const hasRequiredKeys = objectHasAllProperties(value, [
                    'name',
                    'pli_interval_type',
                    'pli_interval_value',
                    'frequency_set',
                    'encryption_key',
                ])

                if (!hasRequiredKeys) {
                    return false
                }

                if (value.frequency_set !== null) {
                    const frequencySet = value.frequency_set
                    const hasRequiredFrequencyKeys = objectHasAllProperties(frequencySet, [
                        'name',
                        'bandwidth',
                        'max_power',
                        'channels',
                    ])

                    if (!hasRequiredFrequencyKeys) {
                        return false
                    }

                    if (frequencySet.channels.constructor !== Array) {
                        return false
                    }

                    const validChannels = frequencySet.channels.every((channel) =>
                        objectHasAllProperties(channel, ['type', 'frequency'])
                    )

                    if (!validChannels) {
                        return false
                    }
                }

                if (value.encryption_key !== null) {
                    if (!Object.hasOwn(value.encryption_key, 'name')) {
                        return false
                    }
                }

                return true
            },
        },

        distribution: {
            type: Object,
            required: true,
            validator: (value) => {
                const hasRequiredKeys = objectHasAllProperties(value, [
                    'name',
                    'distribute_at',
                    'activate_at',
                    'expire_at',
                    'groups',
                    'assets',
                ])

                if (!hasRequiredKeys) {
                    return false
                }

                if (value.groups.constructor !== Array) {
                    return false
                }

                return value.groups.every((group) => objectHasAllProperties(group, ['id', 'name']))
            },
        },
    },

    data() {
        return {}
    },

    computed: {
        dataChannels() {
            return this.getFrequencySetChannels('data')
        },
        controlChannels() {
            return this.getFrequencySetChannels('control')
        },
        timezoneLabel() {
            const { timeZone } = Intl.DateTimeFormat().resolvedOptions()
            let label = 'Unknown'

            if (timeZone) {
                label = timeZone.replace('_', ' ')
            }

            return label
        },
        distributeAtFormatted() {
            return this.formatDate(this.distribution.distribute_at)
        },
        activateAtFormatted() {
            return this.formatDate(this.distribution.activate_at)
        },
        expireAtFormatted() {
            return this.formatDate(this.distribution.expire_at)
        },
        groupsSorted() {
            return sortBy([...this.distribution.groups], (group) => group.name.toUpperCase())
        },
        assetsSorted() {
            return sortBy([...this.distribution.assets], (asset) => asset.name.toUpperCase())
        },
        pliInterval() {
            const interval = formatPliInterval(
                this.configurationSet.pli_interval_type,
                this.configurationSet.pli_interval_value
            )

            return interval ?? { value: '', unit: '' }
        },
    },

    methods: {
        formatDate(date) {
            return !date ? 'N/A' : window.moment(date).format('HH:mm [on] ddd MM/DD/YY')
        },
        getFrequencySetChannels(type) {
            if (this.configurationSet?.frequency_set) {
                return this.configurationSet.frequency_set.channels.filter(
                    (channel) => channel.type?.toLowerCase() === type.toLowerCase()
                )
            }
            return []
        },
    },
}
</script>

<style scoped>
.confirmation-container {
    font-size: 14px;
    font-family: Avenir, serif;
    display: flex;
    gap: 12px;
    flex-direction: column;

    h2 {
        font-size: 16px;
        font-weight: 800;
    }

    dl {
        display: flex;
        flex-direction: column;
        gap: 13px;

        > div {
            display: flex;
            gap: 6px;

            &.multiline {
                flex-direction: column;
            }

            &.remove-top-gap {
                margin-top: -6px;
            }

            &.has-image,
            &.nested {
                position: relative;
                padding-left: 2.2em;
            }

            &.has-image::before {
                content: '';
                position: absolute;
                left: 0;
                top: -3px;
                width: 1.5em;
                height: 1.5em;
                background-repeat: no-repeat;
                background-position: top left;
                background-size: contain;
            }

            &.image-pli::before {
                background-image: url('/images/pli-interval-icon.svg');
            }

            &.image-frequency-set::before {
                background-image: url('/images/frequency-set-icon.svg');
            }

            &.image-encryption-key::before {
                background-image: url('/images/lock-icon.svg');
                top: -5px;
            }

            &.image-groups::before {
                background-image: url('/images/group-icon.svg');
                top: -1px;
            }

            &.image-dates::before {
                background-image: url('/images/date-icon.svg');
            }
        }

        dt {
            font-weight: 500;
            flex-shrink: 0;
        }

        dd ul {
            display: flex;
            flex-direction: column;
            gap: 4px;
        }
    }

    .image-frequency-set .p-badge {
        /* the badge is taller than the text, so centre it and keep it from growing
           the row, which would push the rest of the list down */
        align-self: center;
        margin-block: -5px;
    }

    .group-list span + span::before {
        content: ', ';
    }

    .group-list {
        line-height: normal;
    }

    .spaced-spans {
        display: inline-flex;
        gap: 0.5ch;
    }
}
</style>
